'use strict';

const libPictView = require('pict-view');

/**
 * DeliverableBoard -- the Deliver board. A kanban over Deliverables, lanes by stage (Scoped ->
 * Accepted, with the resolved kit labels). One read (DeliverableBoardAPI.dashboard ->
 * AppData.DeliverableBoard) backs the page. HTML5 drag-and-drop moves a card to another stage; the
 * drop PUTs the deliverable's Status/SortOrder, which trips the server-side status-change audit
 * (WorkflowStatusEvent). Clicking a card opens its detail (nesting, check-off, the connections grid).
 */
class PictViewDeliverableBoard extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._drag = null;
	}

	_board() { return this.pict.providers.DeliverableBoardAPI; }
	_router() { return this.pict.providers['Pict-Router']; }

	show()
	{
		let tmpBoard = this._board();
		if (!tmpBoard) { return Promise.resolve(); }
		return tmpBoard.dashboard().then(() => { this._rebuild(); this.render(); });
	}
	refresh() { return this.show(); }

	// Decorate the raw cards (number label, sub-deliverable count) and fold the lanes into ViewLanes
	// with a per-lane count. The provider owns the raw AppData.DeliverableBoard.Lanes.
	_rebuild()
	{
		let tmpStore = (this.pict.AppData && this.pict.AppData.DeliverableBoard) || null;
		if (!tmpStore) { return; }
		let fDecorate = (pCard) =>
		{
			pCard.NumberLabel = pCard.DeliverableNumber ? ('#' + pCard.DeliverableNumber) : '';
			pCard.SubLabel = pCard.ChildCount ? (pCard.ChildCount + (pCard.ChildCount === 1 ? ' part' : ' parts')) : '';
			pCard.PartOfMark = pCard.IsChild ? 'part of another' : '';
			return pCard;
		};
		tmpStore.ViewLanes = (tmpStore.Lanes || []).map((pLane) =>
		{
			let tmpCards = (pLane.Cards || []).map(fDecorate);
			return { Key: pLane.Key, Label: pLane.Label, Cards: tmpCards, Count: tmpCards.length };
		});
		tmpStore.Empty = (tmpStore.ViewLanes.some((pLane) => pLane.Count > 0)) ? [] : [{ Message: 'No deliverables yet. Add what a customer expects to receive.' }];
	}

	// Surface a message to the user. The board already toasts on a failed drop; new-deliverable failures
	// used to be swallowed, so the button looked dead when the create was refused (a 403 from a persona
	// without create rights) or errored. Always tell the user what happened instead of doing nothing.
	_toast(pMsg, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function')
		{
			tmpModal.toast(pMsg, { type: pType || 'info', duration: (pType === 'error') ? 6000 : 2500 });
		}
	}

	newDeliverable()
	{
		let tmpAPI = this.pict.providers.DeliverableAPI;
		let tmpRouter = this._router();
		if (!tmpAPI)
		{
			this._toast('Could not add a deliverable: the Deliverable service is not available. Reload the page and try again.', 'error');
			return;
		}
		// EntityAPI.create rejects on any non-2xx (throws 'HTTP <status>'); catch it and tell the user
		// rather than letting the button silently do nothing.
		tmpAPI.create({ Title: 'New deliverable', Status: 'Scoped' }).then((pRow) =>
		{
			if (pRow && pRow.IDDeliverable && tmpRouter) { tmpRouter.navigate('/deliverable/' + pRow.IDDeliverable); }
			else if (pRow && pRow.IDDeliverable) { this.show(); }
			else { this._toast('Could not add a deliverable: the server did not return a new record.', 'error'); }
		}).catch((pErr) =>
		{
			this._toast('Could not add a deliverable: ' + ((pErr && pErr.message) || pErr), 'error');
		});
	}

	openDeliverable(pID)
	{
		let tmpRouter = this._router();
		if (tmpRouter && pID) { tmpRouter.navigate('/deliverable/' + pID); }
	}

	dragStart(pEvent, pID)
	{
		this._drag = { ID: pID };
		if (pEvent && pEvent.dataTransfer)
		{
			pEvent.dataTransfer.effectAllowed = 'move';
			try { pEvent.dataTransfer.setData('text/plain', String(pID)); } catch (pErr) { /* ignore */ }
		}
	}

	allowDrop(pEvent)
	{
		if (pEvent && typeof pEvent.preventDefault === 'function') { pEvent.preventDefault(); }
		if (pEvent && pEvent.dataTransfer) { pEvent.dataTransfer.dropEffect = 'move'; }
	}

	drop(pEvent, pLaneKey)
	{
		if (pEvent && typeof pEvent.preventDefault === 'function') { pEvent.preventDefault(); }
		let tmpDrag = this._drag;
		this._drag = null;
		let tmpBoard = this._board();
		if (!tmpDrag || !tmpBoard) { return; }
		let tmpSort = tmpBoard.nextSortOrder(pLaneKey);
		tmpBoard.moveCard(tmpDrag.ID, pLaneKey, tmpSort)
			.then(() => this.show())
			.catch((pErr) => { this._toast('Move failed: ' + ((pErr && pErr.message) || pErr), 'error'); });
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewDeliverableBoard;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-DeliverableBoard',
	DefaultRenderable: 'DeliverableBoard-Display',
	AutoRender: false,
	CSS: /*css*/`
		.ps-dlv { height: 100%; display: flex; flex-direction: column; gap: 14px; }
		.ps-dlv-head { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
		.ps-dlv-head h2 { font-size: 22px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140); margin: 0; }
		.ps-dlv-new { font: inherit; font-size: 13px; border: 1px solid var(--theme-color-brand-primary, #2880a6); background: var(--theme-color-brand-primary, #2880a6); color: var(--theme-color-text-on-brand, #fff); border-radius: 7px; padding: 6px 13px; cursor: pointer; }
		.ps-dlv-new:hover { filter: brightness(1.06); }
		.ps-dlv-stats { display: flex; gap: 8px; flex-wrap: wrap; margin-left: auto; }
		.ps-dlv-stat { background: var(--theme-color-background-secondary, #eef1f6); color: var(--theme-color-text-secondary, #5b6376); border-radius: 999px; padding: 4px 11px; font-size: 12px; }
		.ps-dlv-stat b { color: var(--theme-color-text-primary, #2c3140); font-weight: 700; }
		/* One scroll region for the whole board (both axes); lanes and card stacks never scroll on
		   their own, so a busy lane can't spawn nested column scrollbars. */
		.ps-dlv-lanes { display: flex; gap: 12px; overflow: auto; align-items: flex-start; flex: 1; min-height: 0; padding-bottom: 8px; }
		.ps-dlv-lane { flex: 0 0 240px; width: 240px; background: var(--theme-color-background-secondary, #f1f3f7); border-radius: 10px; padding: 10px; display: flex; flex-direction: column; }
		.ps-dlv-lane-head { display: flex; align-items: center; justify-content: space-between; font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5b6376); padding: 4px 6px 10px; }
		.ps-dlv-lane-count { background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #5b6376); border-radius: 999px; padding: 1px 8px; font-size: 12px; }
		.ps-dlv-lane-cards { display: flex; flex-direction: column; gap: 8px; min-height: 40px; }
		.ps-dlv-card { background: var(--theme-color-background-panel, #fff); border-radius: 8px; padding: 9px 11px; border-left: 3px solid var(--theme-color-brand-primary, #7d63b8); cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.06); }
		.ps-dlv-card:hover { box-shadow: 0 2px 6px rgba(0,0,0,0.12); }
		.ps-dlv-card[draggable="true"]:active { cursor: grabbing; }
		.ps-dlv-card-top { display: flex; align-items: baseline; gap: 6px; margin-bottom: 5px; }
		.ps-dlv-card-num { font-size: 11px; color: var(--theme-color-text-muted, #97a1ab); font-weight: 700; }
		.ps-dlv-card-title { font-size: 14px; color: var(--theme-color-text-primary, #2c3140); line-height: 1.25; }
		.ps-dlv-card-meta { display: flex; gap: 5px; flex-wrap: wrap; }
		.ps-dlv-chip { font-size: 11px; padding: 1px 7px; border-radius: 999px; background: var(--theme-color-background-secondary, #eef1f6); color: var(--theme-color-text-secondary, #5b6376); }
		.ps-dlv-chip:empty { display: none; }
		.ps-dlv-chip-cust { background: transparent; color: var(--theme-color-text-muted, #97a1ab); padding-left: 0; }
		.ps-dlv-chip-exp { background: var(--theme-color-background-tertiary, #e3e8f0); }
		.ps-dlv-empty { color: var(--theme-color-text-muted, #97a1ab); font-size: 14px; padding: 24px 6px; }
	`,
	Templates:
	[
		{
			Hash: 'DeliverableBoard-Display',
			Template: /*html*/`
<div class="ps-page ps-dlv">
	<div class="ps-dlv-head">
		<h2>Deliver</h2>
		<button class="ps-dlv-new" onclick="_Pict.views['Plansheet-DeliverableBoard'].newDeliverable()">+ New deliverable</button>
		<div class="ps-dlv-stats">
			<span class="ps-dlv-stat"><b>{~D:AppData.DeliverableBoard.Counts.Total~}</b> total</span>
			<span class="ps-dlv-stat"><b>{~D:AppData.DeliverableBoard.Counts.Open~}</b> in flight</span>
			<span class="ps-dlv-stat"><b>{~D:AppData.DeliverableBoard.Counts.Accepted~}</b> accepted</span>
		</div>
	</div>
	<div class="ps-dlv-lanes">{~TS:DeliverableBoard-Lane:AppData.DeliverableBoard.ViewLanes~}</div>
	{~TS:DeliverableBoard-Empty:AppData.DeliverableBoard.Empty~}
</div>`
		},
		{
			Hash: 'DeliverableBoard-Lane',
			Template: /*html*/`
<div class="ps-dlv-lane" ondragover="_Pict.views['Plansheet-DeliverableBoard'].allowDrop(event)" ondrop="_Pict.views['Plansheet-DeliverableBoard'].drop(event,'{~D:Record.Key~}')">
	<div class="ps-dlv-lane-head"><span>{~D:Record.Label~}</span><span class="ps-dlv-lane-count">{~D:Record.Count~}</span></div>
	<div class="ps-dlv-lane-cards">{~TS:DeliverableBoard-Card:Record.Cards~}</div>
</div>`
		},
		{
			Hash: 'DeliverableBoard-Card',
			Template: /*html*/`
<div class="ps-dlv-card" draggable="true" ondragstart="_Pict.views['Plansheet-DeliverableBoard'].dragStart(event,{~D:Record.IDDeliverable~})" onclick="_Pict.views['Plansheet-DeliverableBoard'].openDeliverable({~D:Record.IDDeliverable~})">
	<div class="ps-dlv-card-top"><span class="ps-dlv-card-num">{~D:Record.NumberLabel~}</span><span class="ps-dlv-card-title">{~D:Record.Title~}</span></div>
	<div class="ps-dlv-card-meta"><span class="ps-dlv-chip ps-dlv-chip-cust">{~D:Record.CustomerName~}</span><span class="ps-dlv-chip ps-dlv-chip-exp">{~D:Record.ExpectationTitle~}</span><span class="ps-dlv-chip">{~D:Record.SubLabel~}</span></div>
</div>`
		},
		{
			Hash: 'DeliverableBoard-Empty',
			Template: /*html*/`<div class="ps-dlv-empty">{~D:Record.Message~}</div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'DeliverableBoard-Display',
			TemplateHash: 'DeliverableBoard-Display',
			ContentDestinationAddress: '#Plansheet-Workspace'
		}
	]
};
